<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useSidebarBadges } from '@/composables/useSidebarBadges'

definePage({
  meta: {
    requiresAuth: true,
    action: 'read',
    subject: 'Parcels',
    title: 'Inbox',
  },
})

const { t } = useI18n()
const { claimsCount, importsCount, unclaimedCount, inboxTotal, refresh } = useSidebarBadges()

const cards = computed(() => [
  {
    key: 'claim',
    title: t('shipment.claim_parcel.title'),
    description: t('inbox.claim_parcel_hint'),
    icon: 'tabler-link',
    color: 'primary',
    to: { name: 'shipment-claim-parcel' },
    count: unclaimedCount.value,
  },
  {
    key: 'chat',
    title: t('chat_id_queue.title'),
    description: t('inbox.chat_id_queue_hint'),
    icon: 'tabler-id-badge-2',
    color: 'info',
    to: { name: 'shipment-customers-identification-queue' },
    count: claimsCount.value,
  },
  {
    key: 'pending',
    title: t('shipment.pending_imports.title'),
    description: t('inbox.pending_imports_hint'),
    icon: 'tabler-file-alert',
    color: 'warning',
    to: { name: 'shipment-pending-imports' },
    count: importsCount.value,
  },
])
</script>

<template>
  <div>
    <div class="d-flex align-center justify-space-between mb-6 flex-wrap gap-3">
      <div>
        <h1 class="text-h4 mb-1">
          {{ t('inbox.title') }}
        </h1>
        <div class="text-body-2 text-disabled">
          {{ t('inbox.subtitle') }}
        </div>
      </div>
      <div class="d-flex align-center gap-2">
        <VChip
          :color="inboxTotal > 0 ? 'error' : 'secondary'"
          variant="tonal"
        >
          {{ inboxTotal }} {{ t('inbox.pending') }}
        </VChip>
        <VBtn
          variant="text"
          size="small"
          prepend-icon="tabler-refresh"
          @click="refresh"
        >
          {{ t('common.refresh') }}
        </VBtn>
      </div>
    </div>

    <VRow>
      <VCol
        v-for="card in cards"
        :key="card.key"
        cols="12"
        md="4"
      >
        <VCard
          :to="card.to"
          link
          class="inbox-card h-100"
        >
          <VCardText>
            <div class="d-flex align-center gap-3 mb-3">
              <VAvatar
                :color="card.color"
                variant="tonal"
                size="44"
                rounded="lg"
              >
                <VIcon :icon="card.icon" />
              </VAvatar>
              <div class="flex-grow-1">
                <div class="text-subtitle-1 font-weight-medium">
                  {{ card.title }}
                </div>
              </div>
              <VChip
                v-if="card.count !== null"
                :color="card.count > 0 ? card.color : 'secondary'"
                size="small"
                variant="tonal"
              >
                {{ card.count }}
              </VChip>
            </div>
            <div class="text-body-2 text-disabled">
              {{ card.description }}
            </div>
            <div class="d-flex justify-end mt-3">
              <VIcon
                icon="tabler-chevron-right"
                size="16"
                class="text-disabled"
              />
            </div>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>
  </div>
</template>

<style scoped>
.inbox-card {
  transition: transform 0.15s ease;
}
.inbox-card:hover {
  transform: translateY(-2px);
}
</style>
